import { createApp } from 'vue';
import { createPinia } from 'pinia';
import Toast from 'vue-toastification';
import * as Sentry from '@sentry/vue';
// Import the CSS or use your own!
import 'vue-toastification/dist/index.css';

import App from '@/App.vue';
import router from '@/router';

import '@/assets/index.css';
import { dropStaleDeployPreloadErrors } from '@/helpers/sentryEventFilters';
import { reloadWhenPreloadFailsBecauseOfNewDeploy } from '@/helpers/staleDeployReload';
declare global {
	interface Window {
		Intercom: (...args: unknown[]) => void;
		featureFlags: FeatureFlags;
	}
}

export type FeatureFlags = {
	socialLogin: boolean;
	microsoftLogin: boolean;
	cleverLogin: boolean;
	classLinkLogin: boolean;
	inviteColleague: boolean;
	classCoTeachers: boolean;
};

const featureFlags: FeatureFlags = {
	socialLogin: false,
	microsoftLogin: false,
	cleverLogin: false,
	classLinkLogin: false,
	inviteColleague: false,
	classCoTeachers: false,
};
const loadFeatureFlags = async () => {
	try {
		const apiResponse = await fetch('/feature-flags');
		const featureFlagResponse = (await apiResponse.json()) as Partial<FeatureFlags>;

		featureFlags.socialLogin = featureFlagResponse.socialLogin === true;
		featureFlags.microsoftLogin = featureFlagResponse.microsoftLogin === true;
		featureFlags.cleverLogin = featureFlagResponse.cleverLogin === true;
		featureFlags.classLinkLogin = featureFlagResponse.classLinkLogin === true;
		featureFlags.inviteColleague = featureFlagResponse.inviteColleague === true;
		featureFlags.classCoTeachers = featureFlagResponse.classCoTeachers === true;
	} catch (error) {
		console.error('Error loading CF feature-flags', error);
	}
};

const VUE_TOASTIFICATION_OPTIONS = {
	// You can set your default options here
	position: 'top-right',
	timeout: 3000,
	closeOnClick: true,
	pauseOnFocusLoss: true,
	pauseOnHover: true,
	draggable: true,
	draggablePercent: 0.6,
	showCloseButtonOnHover: true,
	closeButton: 'button',
	icon: true,
	rtl: false,
};

loadFeatureFlags()
	.then(() => {
		window.featureFlags = featureFlags;
	})
	.then(() => {
		return createApp(App);
	})
	.then((app) => {
		const isProductionMode = import.meta.env.VITE_MODE?.trim().toLowerCase() === 'production';
		if (isProductionMode) {
			Sentry.init({
				app: app,
				dsn: 'https://6440ee7d285adbcb59b9610d686a1087@o1201161.ingest.us.sentry.io/4508696728633344',
				integrations: [],
				beforeSend: dropStaleDeployPreloadErrors,
			});
		}
		return app;
	})
	.then((app) => {
		window.addEventListener('vite:preloadError', reloadWhenPreloadFailsBecauseOfNewDeploy);
		return app;
	})
	.then((app) => {
		app.use(Toast, VUE_TOASTIFICATION_OPTIONS);
		app.use(createPinia());
		app.use(router);
		app.mount('#app');
	})
	.catch((error) => {
		console.error('Could not start the vue application:', error);
	});
